const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

// Authenticated fetch wrapper - attaches JWT token and handles 401
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
    throw new Error('Session expired. Please sign in again.');
  }

  return response;
};

// Helper function to calculate date range for last N days
const getDateRange = (days = 7) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

// Get default date range (last 7 days)
export const getDefaultDateRange = () => getDateRange(7);

// Fetch book statistics
export const fetchBookStatistics = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/statistics`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch book statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching book statistics:', error);
    throw error;
  }
};

// Fetch published books per day (returns { items, earliestDate, latestDate })
export const fetchPublishedBooksPerDay = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/published-per-day`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch published books per day');
    return await response.json();
  } catch (error) {
    console.error('Error fetching published books per day:', error);
    throw error;
  }
};

// Fetch published books per day with language breakdown (returns { items, earliestDate, latestDate })
export const fetchPublishedBooksPerDayByLanguage = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/published-per-day-by-language`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch published books per day by language');
    return await response.json();
  } catch (error) {
    console.error('Error fetching published books per day by language:', error);
    throw error;
  }
};

// Fetch books per reading level
export const fetchBooksPerReadingLevel = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/per-reading-level`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books per reading level');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books per reading level:', error);
    throw error;
  }
};

// Fetch books per author (top N)
export const fetchBooksPerAuthor = async (startDate, endDate, limit = 10) => {
  try {
    let url = `${API_BASE_URL}/books/per-author`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('limit', limit);
    url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books per author');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books per author:', error);
    throw error;
  }
};

// Fetch books per language
export const fetchBooksPerLanguage = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/per-language`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books per language');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books per language:', error);
    throw error;
  }
};

// Fetch books per book type
export const fetchBooksPerBookType = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/per-book-type`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books per book type');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books per book type:', error);
    throw error;
  }
};

// Fetch books per age rating
export const fetchBooksPerAgeRating = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/books/per-age-rating`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books per age rating');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books per age rating:', error);
    throw error;
  }
};

// Fetch download statistics
export const fetchDownloadStatistics = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/downloads/statistics`);
    if (!response.ok) throw new Error('Failed to fetch download statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching download statistics:', error);
    throw error;
  }
};

// Fetch downloads per day (returns { items, earliestDate, latestDate })
export const fetchDownloadsPerDay = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/downloads/per-day`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch downloads per day');
    return await response.json();
  } catch (error) {
    console.error('Error fetching downloads per day:', error);
    throw error;
  }
};

// Fetch paginated book downloads list (each book + download count)
export const fetchBookDownloadsList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/downloads/book-downloads?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch book downloads list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching book downloads list:', error);
    throw error;
  }
};

// Fetch paginated user downloads list (each user + download count)
export const fetchUserDownloadsList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/downloads/user-downloads?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch user downloads list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user downloads list:', error);
    throw error;
  }
};

// Fetch paginated list of books downloaded by a specific user
export const fetchUserDownloadedBooks = async (userId, page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/downloads/user/${userId}/books?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch user downloaded books');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user downloaded books:', error);
    throw error;
  }
};

// Export book downloads as CSV (backend-generated)
export const exportBookDownloadsCsv = async (sortOrder = 'DESC', searchTerm = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/downloads/export-book-downloads-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export book downloads CSV');
  return response;
};

// Export user downloads as CSV (backend-generated)
export const exportUserDownloadsCsv = async (sortOrder = 'DESC', searchTerm = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/downloads/export-user-downloads-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export user downloads CSV');
  return response;
};

// Export user downloaded books as CSV (backend-generated)
export const exportUserDownloadedBooksCsv = async (userId, sortOrder = 'DESC', searchTerm = '') => {
  let url = `${API_BASE_URL}/downloads/export-user-downloaded-books-csv/${userId}?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export user downloaded books CSV');
  return response;
};

// Fetch user statistics
export const fetchUserStatistics = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/users/statistics`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch user statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user statistics:', error);
    throw error;
  }
};

// Fetch paginated list of all published books (Books Data table)
export const fetchBooksList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/books/list?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch books list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching books list:', error);
    throw error;
  }
};

// Fetch paginated list of published books with full details (Published Books Data table)
export const fetchPublishedBooksList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/books/published-list?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch published books list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching published books list:', error);
    throw error;
  }
};

// Export books data as CSV (server-side)
export const exportBooksCsv = async (sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/books/export-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (language) url += `&language=${encodeURIComponent(language)}`;
  if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export books CSV');
  return response;
};

// Export published books data as CSV (server-side)
export const exportPublishedBooksCsv = async (sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/books/export-published-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (language) url += `&language=${encodeURIComponent(language)}`;
  if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export published books CSV');
  return response;
};

// Fetch paginated list of unique book tags (English)
export const fetchBookTagsList = async (page = 1, pageSize = 100, sortOrder = 'ASC', searchTerm = '') => {
  try {
    let url = `${API_BASE_URL}/books/tags?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch book tags list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching book tags list:', error);
    throw error;
  }
};

// Export book tags as CSV (server-side)
export const exportBookTagsCsv = async (sortOrder = 'ASC', searchTerm = '') => {
  let url = `${API_BASE_URL}/books/export-tags-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export book tags CSV');
  return response;
};

// Fetch paginated list of series
export const fetchSeriesList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/series/list?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch series list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching series list:', error);
    throw error;
  }
};

// Export series data as CSV (server-side)
export const exportSeriesCsv = async (sortOrder = 'DESC', searchTerm = '', language = '', ageRating = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/series/export-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (language) url += `&language=${encodeURIComponent(language)}`;
  if (ageRating) url += `&ageRating=${encodeURIComponent(ageRating)}`;
  if (startDate) url += `&startDate=${startDate}`;
  if (endDate) url += `&endDate=${endDate}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export series CSV');
  return response;
};

// Fetch series per language
export const fetchSeriesPerLanguage = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/series/per-language`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch series per language');
    return await response.json();
  } catch (error) {
    console.error('Error fetching series per language:', error);
    throw error;
  }
};

// Fetch series per reading level
export const fetchSeriesPerReadingLevel = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/series/per-reading-level`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch series per reading level');
    return await response.json();
  } catch (error) {
    console.error('Error fetching series per reading level:', error);
    throw error;
  }
};

// Fetch top series by book count
export const fetchTopSeriesByBookCount = async (startDate, endDate, limit = 10) => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await authFetch(`${API_BASE_URL}/series/top-by-book-count?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch top series by book count');
    return await response.json();
  } catch (error) {
    console.error('Error fetching top series by book count:', error);
    throw error;
  }
};

// Fetch recent created series
export const fetchRecentCreatedSeries = async (startDate, endDate, limit = 5) => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await authFetch(`${API_BASE_URL}/series/recent-created?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch recent created series');
    return await response.json();
  } catch (error) {
    console.error('Error fetching recent created series:', error);
    throw error;
  }
};

// Fetch user registrations per day (returns { items, earliestDate, latestDate })
export const fetchUserRegistrationsPerDay = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/users/registrations-per-day`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch user registrations per day');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user registrations per day:', error);
    throw error;
  }
};

// Fetch paginated list of accounts
export const fetchAccountsList = async (page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', isActive = '', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/users/list?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (isActive !== '') url += `&isActive=${isActive}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch accounts list');
    return await response.json();
  } catch (error) {
    console.error('Error fetching accounts list:', error);
    throw error;
  }
};

// Export accounts data as CSV (server-side)
export const exportAccountsCsv = async (sortOrder = 'DESC', searchTerm = '', isActive = '', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/users/export-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  if (isActive !== '') url += `&isActive=${isActive}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export accounts CSV');
  return response;
};

// Fetch paginated list of books for a specific series
export const fetchSeriesBooks = async (seriesId, page = 1, pageSize = 100, sortOrder = 'DESC', searchTerm = '', language = '') => {
  try {
    let url = `${API_BASE_URL}/series/${seriesId}/books?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch series books');
    return await response.json();
  } catch (error) {
    console.error('Error fetching series books:', error);
    throw error;
  }
};

// Export series books data as CSV (server-side)
export const exportSeriesBooksCsv = async (seriesId, sortOrder = 'DESC', searchTerm = '') => {
  let url = `${API_BASE_URL}/series/${seriesId}/books/export-csv?sortOrder=${sortOrder}`;
  if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export series books CSV');
  return response;
};

// Fetch ETS view event statistics (active users, view counts)
export const fetchEtsViewStatistics = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/ets-analytics/statistics`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch ETS view statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching ETS view statistics:', error);
    throw error;
  }
};

// Fetch reading level statistics for chart
export const fetchReadingLevelStatistics = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/ets-analytics/reading-levels`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch reading level statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching reading level statistics:', error);
    throw error;
  }
};

// Fetch platform statistics (Web vs iOS vs Android)
export const fetchPlatformStatistics = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/ets-analytics/platform-statistics`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch platform statistics');
    return await response.json();
  } catch (error) {
    console.error('Error fetching platform statistics:', error);
    throw error;
  }
};

// Fetch paginated ETS daily reading details
export const fetchEtsDailyDetails = async (page = 1, pageSize = 100, sortOrder = 'DESC', startDate = '', endDate = '') => {
  try {
    let url = `${API_BASE_URL}/ets-analytics/daily-details?page=${page}&pageSize=${pageSize}&sortOrder=${sortOrder}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch ETS daily details');
    return await response.json();
  } catch (error) {
    console.error('Error fetching ETS daily details:', error);
    throw error;
  }
};

// Fetch all ETS daily reading points (ascending by date) for the readings chart
export const fetchEtsDailyChart = async (startDate = '', endDate = '') => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await authFetch(`${API_BASE_URL}/ets-analytics/daily-chart?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch ETS daily chart data');
    return await response.json();
  } catch (error) {
    console.error('Error fetching ETS daily chart data:', error);
    throw error;
  }
};

// Export ETS daily details as CSV
export const exportEtsDailyDetailsCsv = async (sortOrder = 'DESC', startDate = '', endDate = '') => {
  let url = `${API_BASE_URL}/ets-analytics/export-daily-details-csv?sortOrder=${sortOrder}`;
  if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  const response = await authFetch(url);
  if (!response.ok) throw new Error('Failed to export ETS daily details CSV');
  return response;
};

// Fetch active users per country for heatmap
export const fetchActiveUsersPerCountry = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/ets-analytics/active-users-per-country`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch active users per country');
    return await response.json();
  } catch (error) {
    console.error('Error fetching active users per country:', error);
    throw error;
  }
};
